// watch/ClipWatch.tsx — watching a clip post (1.27.60): the source video, playing only the
// clipped section on a loop, with who clipped it, their note, a way to the full video, and
// the clip post's own comments. Plays exactly what the source would show this reader — a
// clip of a video they can't see is just a note (the server sends {unavailable}).
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { MetaRow } from '@/ui';
import type { Post } from '@/core/models/post';
import { CommentsSection } from '@/features/post-detail/CommentsSection';
import { DiscussionToolsProvider } from '@/features/discussion-tools/DiscussionToolsProvider';
import { VideoPlayer } from '../../player/VideoPlayer';
import { formatClock } from '../../player/chapters';

const Page = styled.div`
  display: grid;
  gap: 14px;
  max-width: 1100px;
  h1 {
    margin: 4px 0 0;
    font: 700 ${({ theme }) => theme.fontSize.lg} ${({ theme }) => theme.font.display};
    color: ${({ theme }) => theme.color.textPrimary};
    overflow-wrap: anywhere;
  }
`;

const Meta = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin: 0;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
  font-variant-numeric: tabular-nums;
  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    background: ${({ theme }) => theme.color.surfaceRaised};
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
    text-decoration: none;
  }
  a:hover {
    filter: brightness(1.12);
  }
`;

const Note = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: pre-wrap;
`;

const Gone = styled.div`
  display: grid;
  place-items: center;
  gap: 10px;
  padding: 60px 16px;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function ClipWatch({ post }: { post: Post }) {
  const clip = post.clip!;
  if (clip.unavailable || !clip.video) {
    return (
      <Gone>
        <p>This clip’s video isn’t available.</p>
        <Link to="/videos">Back to Videos</Link>
      </Gone>
    );
  }
  const start = clip.startMs / 1000;
  const end = clip.endMs / 1000;
  return (
    <DiscussionToolsProvider post={post}>
      <Page data-testid="clip-watch">
        <VideoPlayer
          video={clip.video}
          title={`Clip: ${clip.sourceTitle}`}
          startAt={start}
          clipRange={{ start, end }}
        />
        <h1>{clip.sourceTitle}</h1>
        <Meta>
          <MetaRow>
            <span>
              <Icon name="scissors" size={14} /> Clip by {post.name}
            </span>
            <span>{post.time}</span>
            <span>
              {formatClock(start)}–{formatClock(end)}
            </span>
          </MetaRow>
          <Link to={`/videos/${clip.sourceId}?t=${Math.floor(start)}`}>
            <Icon name="play" size={14} /> Watch the full video from {clip.sourceAuthorHandle}
          </Link>
        </Meta>
        {post.body.trim() && <Note>{post.body}</Note>}
        <CommentsSection postId={post.id} />
      </Page>
    </DiscussionToolsProvider>
  );
}
